import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Bell, Loader2, Sparkles } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const CONDITIONS = [
  { key: "price_above", label: "Price rises above", suffix: "$", placeholder: "e.g. 250" },
  { key: "price_below", label: "Price falls below", suffix: "$", placeholder: "e.g. 200" },
  { key: "pct_change_above", label: "24h gain ≥", suffix: "%", placeholder: "e.g. 5" },
  { key: "pct_change_below", label: "24h drop ≤", suffix: "%", placeholder: "e.g. -5" },
  { key: "breakout_1m", label: "1-min breakout ≥", suffix: "%", placeholder: "e.g. 1" },
  { key: "sentiment_above", label: "Sentiment ≥", suffix: "score", placeholder: "e.g. 15" },
  { key: "sentiment_below", label: "Sentiment ≤", suffix: "score", placeholder: "e.g. -15" },
  { key: "rsi_overbought", label: "RSI overbought ≥", suffix: "", placeholder: "70" },
  { key: "rsi_oversold", label: "RSI oversold ≤", suffix: "", placeholder: "30" },
];

const DEFAULTS = {
  price_above: (price) => (price ? (price * 1.1).toFixed(2) : ""),
  price_below: (price) => (price ? (price * 0.9).toFixed(2) : ""),
  pct_change_above: () => "5",
  pct_change_below: () => "-5",
  breakout_1m: () => "1",
  sentiment_above: () => "15",
  sentiment_below: () => "-15",
  rsi_overbought: () => "70",
  rsi_oversold: () => "30",
};

export default function AlertDialog({
  open,
  onOpenChange,
  symbol,
  name,
  assetType,
  currentPrice,
  onCreated,
}) {
  const [condition, setCondition] = useState("price_above");
  const [threshold, setThreshold] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const cond = useMemo(() => CONDITIONS.find((c) => c.key === condition), [condition]);

  useEffect(() => {
    if (open) {
      setCondition("price_above");
      setThreshold(DEFAULTS.price_above(currentPrice));
      // Load smart suggestions
      setLoadingSuggestions(true);
      api
        .get(`/alerts/suggestions/${assetType}/${symbol}`)
        .then((r) => setSuggestions(r.data.suggestions || []))
        .catch(() => setSuggestions([]))
        .finally(() => setLoadingSuggestions(false));
    }
  }, [open, currentPrice, assetType, symbol]);

  useEffect(() => {
    setThreshold(DEFAULTS[condition](currentPrice));
  }, [condition, currentPrice]);

  const submit = async () => {
    const val = parseFloat(threshold);
    if (Number.isNaN(val)) {
      toast.error("Enter a valid number");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/alerts", {
        symbol,
        name,
        asset_type: assetType,
        condition_type: condition,
        threshold: val,
      });
      toast.success("Alert created — we'll notify you when it triggers");
      onOpenChange(false);
      if (onCreated) onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create alert");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-2xl border-[#EAE6E1]" data-testid="alert-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight">
            <Bell className="w-5 h-5 inline mr-2 text-[#D18E63]" strokeWidth={1.5} />
            Set price alert
          </DialogTitle>
          <DialogDescription className="text-[#6B7068]">
            Get notified when {symbol} hits a target. We'll check every minute and ping the bell + a desktop notification (if enabled).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {(loadingSuggestions || suggestions.length > 0) && (
            <div data-testid="alert-suggestions">
              <Label className="text-xs uppercase tracking-[0.2em] text-[#6B7068] flex items-center gap-1.5">
                <Sparkles className="w-3 h-3 text-[#D18E63]" strokeWidth={1.75} />
                Smart suggestions
              </Label>
              {loadingSuggestions ? (
                <div className="mt-1.5 text-xs text-[#6B7068] flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" /> Reading the chart...
                </div>
              ) : (
                <div className="mt-1.5 flex gap-2 flex-wrap">
                  {suggestions.map((s) => (
                    <button
                      key={`${s.condition_type}-${s.threshold}`}
                      type="button"
                      data-testid={`alert-suggestion-${s.condition_type}`}
                      onClick={() => {
                        setCondition(s.condition_type);
                        setThreshold(String(s.threshold));
                      }}
                      title={s.reasoning}
                      className="text-xs rounded-full bg-[#FFF8F1] border border-[#D18E63]/30 hover:border-[#D18E63] hover:bg-[#FFF1E1] px-3 py-1.5 text-[#2A2F2B] transition-soft"
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#6B7068]">
              Condition
            </Label>
            <Select value={condition} onValueChange={setCondition}>
              <SelectTrigger
                className="mt-1.5 rounded-xl border-[#EAE6E1] bg-white"
                data-testid="alert-condition-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {CONDITIONS.map((c) => {
                  // RSI only really meaningful for stocks (we still allow crypto, indicator works)
                  return (
                    <SelectItem
                      key={c.key}
                      value={c.key}
                      data-testid={`alert-condition-${c.key}`}
                    >
                      {c.label}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.2em] text-[#6B7068]">
              Threshold
            </Label>
            <div className="mt-1.5 flex items-center gap-2">
              {cond.suffix === "$" && (
                <span className="font-mono-ticker text-[#6B7068]">$</span>
              )}
              <Input
                type="number"
                step="any"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                placeholder={cond.placeholder}
                data-testid="alert-threshold-input"
                className="rounded-xl border-[#EAE6E1] bg-white"
              />
              {cond.suffix === "%" && (
                <span className="font-mono-ticker text-[#6B7068]">%</span>
              )}
            </div>
            {currentPrice != null && cond.suffix === "$" && (
              <div className="text-xs text-[#6B7068] mt-1.5 font-mono-ticker">
                Current price: ${currentPrice.toFixed(2)}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="alert-cancel-btn"
            className="rounded-full"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            data-testid="alert-submit-btn"
            className="rounded-full bg-[#2A2F2B] hover:bg-[#547A8F] text-white"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                Creating...
              </>
            ) : (
              "Create alert"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
